// eslint-disable-next-line import/no-cycle
import transferService from '@client/services/transfers';
import BankAccounts from '@client/models/store/bankAccount';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import MicroDepositStatus from '@client/enums/micro-deposit-status-type';
import {BankAccountIdentityStatus} from '@simpleinvest/ms-custodian-sdk';

const logger = new Logger({
  category: Category.BankLinks,
  fileName: 'bankAccount.js',
});

const stateObj = () => new BankAccounts();

const actions = {
  async getBankAccounts({commit, rootGetters}) {
    const {'user/clUserId': clUserId} = rootGetters;

    try {
      const response = await transferService.getLinkedBankAccounts(clUserId);

      commit('setBankAccounts', response.data.data.bankAccounts);
      commit('setIsLoaded', true);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getBankAccounts',
        message: `getBankAccounts error caught`,
      });
      logger.logException({error});
    }
  },
  setIsLoaded(context, payload) {
    context.commit('setIsLoaded', payload.loaded);
  },
};

const mutations = {
  setBankAccounts(state, bankAccounts) {
    bankAccounts.forEach((bankAccount) => {
      // eslint-disable-next-line no-param-reassign
      delete bankAccount.bankAccountNumber;
    });
    state.bankAccounts = bankAccounts;
  },
  setIsLoaded(state, loaded) {
    state.loaded = loaded;
  },
};

const gettersObj = {
  pendingActions: (state) => (bankAccountId) => {
    const bankAccount = state.bankAccounts?.find((acct) => acct.id === bankAccountId) || {};

    const microDepositPending = MicroDepositStatus.PendingManual === bankAccount.microDepositRequestStatus;

    const identityPending = [
      BankAccountIdentityStatus.Pending,
      BankAccountIdentityStatus.VerifyDocumentation,
      BankAccountIdentityStatus.DocumentationRequired,
    ].includes(bankAccount.identityRequestStatus);

    return {
      microDepositPending,
      identityPending,
    };
  },
  pendingReason: (state, getters) => (bankAccountId) => {
    const {microDepositRequestStatus, identityRequestStatus} = getters.getBankAccountById(bankAccountId)?.[0] || {};

    if ([MicroDepositStatus.PendingManual, MicroDepositStatus.PendingAutomatic].includes(microDepositRequestStatus)) {
      return microDepositRequestStatus;
    }

    const identityPending = [
      BankAccountIdentityStatus.Pending,
      BankAccountIdentityStatus.VerifyDocumentation,
      BankAccountIdentityStatus.DocumentationRequired,
    ].includes(identityRequestStatus);

    if (identityPending) {
      return identityRequestStatus;
    }

    return null;
  },
  hasBankActions: (state, getters) => getters.bankAccounts.some((bank) => bank.plaidItemUpdateRequests?.length),
  hasBankAccounts: (state) => !!state.bankAccounts?.length,
  bankAccounts: (state) => state.bankAccounts,
  loaded: (state) => state.loaded,
  getBankAccountById: (state, getters) => (bankAccountId) =>
    getters.bankAccounts.filter((account) => Number(account.id) === Number(bankAccountId)),
};

export default {
  namespaced: true,
  state: stateObj,
  mutations,
  actions,
  getters: gettersObj,
};
